import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { Grid, Button, Typography, Divider, useTheme, Box } from '@mui/material'
import { toast } from 'react-toastify'
import useCustomStyles from '../../utils/hooks/CustomStylesHook'
import { useAppSelector } from '../../app/hooks'
import {
  selectSelectedLocationId,
  selectLocationById,
  getLocationData,
} from '../location/locationSlice'
import {
  fullRegistrationFormSections,
  quickRegistrationFormSections,
} from './RegistrationConstants'
import { createPatient, updatePatient } from '../../services/PatientService'
import { INTENT_DISTRICTS, INTENT_STATES } from '../ipRegistration/Constants'
import { startSxpProxy } from '../../utils/api'
import { IPD_PROJECT_ID } from '../../utils/constants'
import { useNavigate } from 'react-router-dom'
import ProfilePictureOne from './ProfilePictureOne'
import {
  Others,
  PERMANENT_ADDRESS_FIELDS,
  permanentAddressConfirmation,
  PATIENTS_ROUTE,
  REGISTRATION_DEFAULT_SERVICE,
  REGISTRATION_FIELD_AGE,
  REGISTRATION_FIELD_CHECKBOX,
  REGISTRATION_FIELD_DISTRICT,
  REGISTRATION_FIELD_DOB,
  REGISTRATION_FIELD_GENDER,
  REGISTRATION_FIELD_SALUTATION,
  REGISTRATION_FIELD_SAME_AS_CURRENT_ADDRESS,
  REGISTRATION_FIELD_STATE,
  REGISTRATION_TYPE_BY_SERVICE,
  RegistrationTypes,
  SALUTATION_GENDER_MAP,
  specifyRelationship,
} from './constants'

const styles = {
  sectionTitle: {
    color: '#4682b4',
    margin: '20px 0 10px',
    fontWeight: 600,
  },
  errorStyles: {
    color: 'red',
    fontSize: '0?.8rem',
  },
  formField: {
    marginBottom: '16px',
    fontSize: '12px !important',
  },
  checkBoxStyles: {
    marginBottom: '16px',
    fontSize: '12px !important',
    display: 'flex',
    alignItems: 'center',
  },
  buttonGroup: {
    width: 'fit-content',
    textTransform: 'capitalize',
    backgroundColor: '#4682b4',
  },
  inputCheckBox: {
    marginRight: '8px',
    width: '16px',
    height: '16px',
    accentColor: '#4682b4',
    cursor: 'pointer',
  },
  input: {
    width: '100%',
    padding: '5px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    marginTop: '4px',
    marginBottom: '4px',
    fontSize: '13px',
  },
  labelStyles: { fontSize: '14px', cursor: 'pointer' },
  hideField: {
    display: 'none',
  },
  fieldLabel: {
    fontWeight: 600,
  },
  requiredMark: {
    color: 'red',
  },
  sectionDivider: {
    marginBottom: '16px',
  },
}

interface FieldConfig {
  name: string
  label: string
  type: string
  required?: boolean
  defaultValue?: any
  options?: { label: string; value: string }[]
  yupValidation: any
}

interface SectionConfig {
  title: string
  fields: FieldConfig[]
}

export interface FormValues {
  // Patient Registration
  service: string
  salutation: string
  firstName: string
  middleName: string
  lastName: string
  gender: string
  dob: string // ISO date string (YYYY-MM-DD)
  age: string // using string for consistency with Formik input, or number if preferred

  // Other Information
  patientSource: string
  phoneNumber: string
  preferredCommunication: string
  alternateNumber: string
  emailId: string
  maritalStatus: string
  socioEconomicStatus: string
  governmentId: string
  idNumber: string
  aadharId: string
  religion: string
  occupation: string

  // Address
  houseNo: string
  area: string
  town: string
  state: string
  district: string
  postalCode: string
  sameAsCurrentAddress: boolean
  permanentHouseNo: string
  permanentArea: string
  permanentTown: string
  permanentState: string
  permanentDistrict: string
  permanentPostalCode: string

  // Emergency Contact
  emergencyAddress: string
}

interface FieldConfig {
  name: string
  label: string
  type: string
  required?: boolean
  options?: { label: string; value: string }[]
}

type PatientRegistrationProps = {
  initialFormValues?: Record<string, any>
}

const PatientRegistration: React.FC<PatientRegistrationProps> = ({
  initialFormValues = {},
}) => {
  const theme = useTheme()
  const navigate = useNavigate()
  const locationId = useAppSelector(selectSelectedLocationId)
  const locationData = useAppSelector((state) =>
    selectLocationById(locationId, state)
  )
  const locationMeta = useMemo(() => {
    if (locationData) {
      const loc = getLocationData(locationData)
      return {
        locationPrefix: loc.prefix,
        selectedLocationId: loc.id,
        selectedLocationName: loc.name,
        locationValue: loc.value,
      }
    }
    return {}
  }, [locationData])
  const [statesList, setStatesList] = useState([])
  const [districtsList, setdistrictsList] = useState([])
  const [profilePicture, setProfilePicture] = useState(
    initialFormValues?.photo || ''
  )
  const [isSubmittingRegistration, setIsSubmittingRegistration] =
    useState(false)
  const [type, setType] = useState(REGISTRATION_DEFAULT_SERVICE)
  const hasAppliedPrefillRef = useRef(false)
  const classes = useCustomStyles(styles, theme)

  const fullRegistrationServices = useMemo(
    () => [
      RegistrationTypes.full,
      RegistrationTypes.membershipPrimary,
      RegistrationTypes.ABMembershipFull,
    ],
    []
  )

  const shouldFieldRender = (fieldName: string, values: any) => {
    if (
      fieldName === specifyRelationship &&
      values?.relationshipWithPatient !== Others
    ) {
      return true
    } else if (
      fieldName?.startsWith('permanent') &&
      fieldName !== permanentAddressConfirmation &&
      values?.sameAsCurrentAddress
    ) {
      return true
    } else {
      return false
    }
  }

  function calculateAgeFromDOB(dobString: string) {
    if (!dobString) return ''
    const today = new Date()
    const dob = new Date(dobString)

    let age = today?.getFullYear() - dob?.getFullYear()
    const monthDiff = today?.getMonth() - dob?.getMonth()
    const dayDiff = today?.getDate() - dob?.getDate()

    if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
      age--
    }

    return age >= 0 ? age?.toString() : ''
  }

  const generateInitialValues = (
    sections: SectionConfig[]
  ): Record<string, any> => {
    const initialValues: Record<string, any> = {}

    sections?.forEach((section) => {
      section?.fields?.forEach((field) => {
        initialValues[field?.name] = field?.defaultValue ?? ''
      })
    })

    return initialValues
  }

  const generateValidations = (
    sections: SectionConfig[]
  ): Record<string, any> => {
    const validations: Record<string, any> = {}

    sections?.forEach((section) => {
      section?.fields?.forEach((field) => {
        validations[field?.name] = field?.yupValidation ?? ''
      })
    })

    return validations
  }

  const getSchema = (type: string) => {
    if (fullRegistrationServices.includes(type as any)) {
      return fullRegistrationFormSections(statesList, districtsList, type)
    }

    return quickRegistrationFormSections(type)
  }

  const schemaForInitialValues: any = getSchema(type)
  const initialValues = useMemo(
    () => generateInitialValues(schemaForInitialValues),
    [schemaForInitialValues]
  )

  const validationSchema = Yup.object().shape({
    ...generateValidations(schemaForInitialValues),
  })

  const registrationPayloadKeys = useMemo(
    () => [
      ...new Set([
        ...schemaForInitialValues.flatMap((section: SectionConfig) =>
          section?.fields?.map((field) => field?.name)
        ),
        'fhirId',
        'identifier',
        'photo',
        'registrationType',
        'locationPrefix',
        'selectedLocationId',
        'selectedLocationName',
        'locationValue',
      ]),
    ],
    [schemaForInitialValues]
  )

  const buildRegistrationPayload = (formValues: Record<string, any>) =>
    registrationPayloadKeys?.reduce((payload, key) => {
      if (formValues?.[key] !== undefined) {
        payload[key] = formValues[key]
      }

      return payload
    }, {} as Record<string, any>)

  const getRegistrationType = (service: string) =>
    REGISTRATION_TYPE_BY_SERVICE?.[service]

  const {
    values,
    errors,
    touched,
    handleChange,
    handleSubmit,
    setFieldValue,
    setValues,
  }: any = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (formValues, { setSubmitting }) => {
      try {
        setIsSubmittingRegistration(true)
        const fhirId = initialFormValues?.fhirId || formValues?.fhirId
        const identifier = initialFormValues?.id || formValues?.id
        const isUpdate = Boolean(fhirId)
        const payload = buildRegistrationPayload({
          ...formValues,
          fhirId,
          ...(isUpdate ? { identifier } : {}),
          photo: profilePicture,
          registrationType: getRegistrationType(formValues?.service),
          ...locationMeta,
        })

        const response = isUpdate
          ? await updatePatient(payload)
          : await createPatient(payload)

        if (!response?.success) {
          toast.error(response?.message || 'Patient registration failed')
          return
        }

        toast.success(
          response?.message ||
            (isUpdate
              ? 'Patient updated successfully'
              : 'Patient registered successfully')
        )
        navigate(PATIENTS_ROUTE)
      } catch (error: any) {
        toast.error(
          error?.response?.data?.message ||
            'Something went wrong while registering patient'
        )
      } finally {
        setSubmitting(false)
        setIsSubmittingRegistration(false)
      }
    },
    enableReinitialize: false,
  })

  // Now safely derive schema and formSections using current values
  const schema: any = getSchema(type)
  const formSections: { title: string; fields: FieldConfig[] }[] = schema

  const getStatesList = useCallback(async () => {
    const intent = INTENT_STATES
    const state = {}
    await startSxpProxy(IPD_PROJECT_ID, intent, state)
      ?.then((data: any) => {
        if (data?.data?.success) {
          const fetchedStates = data?.data?.data?.states || []
          const formattedStates = fetchedStates?.map((state: any) => ({
            label: state?.state_name,
            value: state?.id?.toString(),
          }))
          setStatesList(formattedStates)
        } else {
          setStatesList([])
        }
      })
      ?.catch((err: any) => {
        console?.error(err)
      })
  }, [])

  useEffect(() => {
    getStatesList()
  }, [getStatesList])

  useEffect(() => {
    setType(values?.service)
  }, [values?.service])

  const applyPrefillValues = useCallback(() => {
    if (hasAppliedPrefillRef.current) {
      return
    }

    if (!initialFormValues || Object.keys(initialFormValues).length === 0) {
      return
    }

    // Don't lock until we have real patient data (edit mode sends firstName)
    const hasMeaningfulData = !!(
      initialFormValues?.firstName || initialFormValues?.phoneNumber
    )
    if (!hasMeaningfulData) {
      return
    }

    const mergedValues: Record<string, any> = {
      ...initialValues,
      ...initialFormValues,
      service:
        initialFormValues?.service ||
        type ||
        initialValues?.service ||
        REGISTRATION_DEFAULT_SERVICE,
    }

    setValues(mergedValues, false)
    setType(mergedValues?.service || REGISTRATION_DEFAULT_SERVICE)
    setProfilePicture(mergedValues?.photo || '')
    hasAppliedPrefillRef.current = true
  }, [initialFormValues, initialValues, setValues, type])

  useEffect(() => {
    applyPrefillValues()
  }, [applyPrefillValues])

  const syncMissingFieldsWithInitialValues = useCallback(() => {
    if (!values) {
      return
    }

    const mergedValues = {
      ...initialValues,
      ...values,
      service: values?.service || type || initialValues?.service,
    }

    const hasMissingField = Object.keys(initialValues).some(
      (fieldName) => values?.[fieldName] === undefined
    )

    if (hasMissingField) {
      setValues(mergedValues, false)
    }
  }, [initialValues, setValues, type, values])

  useEffect(() => {
    syncMissingFieldsWithInitialValues()
  }, [syncMissingFieldsWithInitialValues])

  const getDistrictsList = useCallback(async (selectedState: string) => {
    if (selectedState) {
      const intent = INTENT_DISTRICTS
      const state = {
        state: selectedState,
      }
      await startSxpProxy(IPD_PROJECT_ID, intent, state)
        ?.then((data: any) => {
          if (data?.data?.success) {
            const fetchedDistricts = data?.data?.data?.districts || []
            const formattedDistricts = fetchedDistricts?.map((dist: any) => ({
              label: dist?.district,
              value: dist?.id?.toString(),
            }))
            setdistrictsList(formattedDistricts)
          } else {
            setdistrictsList([])
          }
        })
        ?.catch((err: any) => {
          console?.error(err)
        })
    }
  }, [])

  // When editing, load districts for the pre-filled state value
  useEffect(() => {
    if (values?.state && districtsList.length === 0) {
      getDistrictsList(values.state)
    }
  }, [values?.state, districtsList.length, getDistrictsList])

  const handleFieldChange = (e: any) => {
    handleChange(e)
    const { name, value, checked, type } = e?.target ?? {}
    const fieldValue = type === REGISTRATION_FIELD_CHECKBOX ? checked : value

    if (
      name === REGISTRATION_FIELD_SALUTATION &&
      SALUTATION_GENDER_MAP[value]
    ) {
      setFieldValue(REGISTRATION_FIELD_GENDER, SALUTATION_GENDER_MAP[value])
    }

    if (name === REGISTRATION_FIELD_STATE) {
      getDistrictsList(value)
      setFieldValue(REGISTRATION_FIELD_DISTRICT, '')
    }
    if (name === REGISTRATION_FIELD_DOB) {
      const age = calculateAgeFromDOB(value)
      setFieldValue(REGISTRATION_FIELD_AGE, age)
    }

    if (name === REGISTRATION_FIELD_SAME_AS_CURRENT_ADDRESS) {
      setFieldValue(
        PERMANENT_ADDRESS_FIELDS.houseNo,
        checked ? values?.houseNo : ''
      )
      setFieldValue(PERMANENT_ADDRESS_FIELDS.area, checked ? values?.area : '')
      setFieldValue(PERMANENT_ADDRESS_FIELDS.town, checked ? values?.town : '')
      setFieldValue(
        PERMANENT_ADDRESS_FIELDS.state,
        checked ? values?.state : ''
      )
      setFieldValue(
        PERMANENT_ADDRESS_FIELDS.district,
        checked ? values?.district : ''
      )
      setFieldValue(
        PERMANENT_ADDRESS_FIELDS.postalCode,
        checked ? values?.postalCode : ''
      )
    }

    // Mirror current address to permanent fields in real-time when checkbox is checked
    if (
      PERMANENT_ADDRESS_FIELDS[name] &&
      values?.[REGISTRATION_FIELD_SAME_AS_CURRENT_ADDRESS]
    ) {
      setFieldValue(PERMANENT_ADDRESS_FIELDS[name], fieldValue)
    }
  }

  return (
    <Grid
      container
      p={4}
      bgcolor={'#ffffff'}
      justifyContent={'center'}
      columnGap={2}
      rowGap={2}
    >
      <Grid
        item
        xs={12}
        sm={12}
        md={8}
        lg={8}
        xl={6}
        boxShadow={10}
        borderRadius={3}
        pl={5}
        pr={5}
        pb={5}
        pt={1}
        order={{ xs: 2, sm: 2, md: 1, lg: 1, xl: 1 }}
      >
        <form onSubmit={handleSubmit}>
          {formSections?.map((section) => (
            <div key={section?.title}>
              <Typography variant='h6' className={classes?.sectionTitle}>
                {section?.title}
              </Typography>
              <Divider className={classes?.sectionDivider} />

              <Grid container spacing={2}>
                {section?.fields?.map((field) => (
                  <Grid
                    item
                    xs={12}
                    sm={field?.type === 'checkbox' ? 12 : 6}
                    key={field?.name}
                    className={`${
                      shouldFieldRender(field?.name, values) &&
                      classes?.hideField
                    }`}
                  >
                    {field?.type === 'select' ? (
                      <div className={classes?.formField}>
                        <label
                          htmlFor={field?.name}
                          className={classes?.fieldLabel}
                        >
                          {field?.label}
                          {field?.required && (
                            <span className={classes?.requiredMark}>*</span>
                          )}
                        </label>
                        <select
                          id={`registration-${field?.name}`}
                          name={field?.name}
                          value={values?.[field?.name]}
                          onChange={handleFieldChange}
                          className={classes?.input}
                          disabled={
                            values?.sameAsCurrentAddress &&
                            field?.name?.startsWith('permanent')
                          }
                        >
                          <option value=''>Select an option</option>
                          {field?.options?.map((option) => (
                            <option key={option?.value} value={option?.value}>
                              {option?.label}
                            </option>
                          ))}
                        </select>
                        {touched?.[field?.name] && errors?.[field?.name] && (
                          <Box className={classes?.errorStyles}>
                            {errors?.[field?.name]}
                          </Box>
                        )}
                      </div>
                    ) : field?.type === 'checkbox' ? (
                      <Box className={classes?.checkBoxStyles}>
                        <Box
                          component={'input'}
                          className={classes?.inputCheckBox}
                          id={field?.name}
                          name={field?.name}
                          type='checkbox'
                          checked={!!values?.[field?.name]}
                          onChange={handleFieldChange}
                        />
                        <Box
                          component={'label'}
                          htmlFor={field?.name}
                          className={classes?.labelStyles}
                        >
                          {field?.label}
                          {field?.required && (
                            <span className={classes?.requiredMark}>*</span>
                          )}
                        </Box>
                      </Box>
                    ) : (
                      <div className={classes?.formField}>
                        <label
                          htmlFor={field?.name}
                          className={classes?.fieldLabel}
                        >
                          {field?.label}
                          {field?.required && (
                            <span className={classes?.requiredMark}>*</span>
                          )}
                        </label>
                        <input
                          id={`registration-${field?.name}`}
                          name={field?.name}
                          type={field?.type}
                          value={values[field?.name]}
                          onChange={handleFieldChange}
                          className={classes?.input}
                          disabled={
                            values?.sameAsCurrentAddress &&
                            field?.name?.startsWith('permanent')
                          }
                        />
                        {touched[field?.name] &&
                          errors[field?.name] &&
                          !(
                            values?.sameAsCurrentAddress &&
                            field?.name?.startsWith('permanent')
                          ) && (
                            <Box className={classes?.errorStyles}>
                              {errors?.[field?.name]}
                            </Box>
                          )}
                      </div>
                    )}
                  </Grid>
                ))}
              </Grid>
            </div>
          ))}
          <Box display={'flex'} justifyContent={'center'}>
            <Button
              type='submit'
              variant='contained'
              color='primary'
              className={classes?.buttonGroup}
              disabled={isSubmittingRegistration}
            >
              {isSubmittingRegistration ? 'Submitting...' : 'Submit'}
            </Button>
          </Box>
        </form>
      </Grid>
      <Grid
        item
        xs={12}
        sm={12}
        md={2}
        lg={2}
        xl={1.5}
        order={{ xs: 1, sm: 1, md: 2, lg: 2, xl: 2 }}
      >
        {/* <ProfilePicture
              photo={profilePicture}
              onPhotoChange={setProfilePicture}
              item='admin'
            /> */}
        <Box display={'flex'} justifyContent={'center'}>
          <ProfilePictureOne
            photo={profilePicture}
            onPhotoChange={setProfilePicture}
          />
        </Box>
      </Grid>
    </Grid>
  )
}

export default PatientRegistration
